import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, User, Phone, Mail, MapPin, Calendar, Send, Check, Building2 } from 'lucide-react';
import { trackPageView, trackEvent } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const products = [
  'Lion Classic (Composite)', 'Lion Supreme (PPC)', 'Hathi Cement OPC 53', 'Hathi Cement PPC',
  'Hathi Cement Composite', 'Siddhee Cement OPC 43', 'Siddhee Cement OPC 53',
  'EFCON AAC Block (Sand)', 'EFCON AAC Block (Flyash)', 'GGBS', 'Fly Ash',
  'Cemtite Tile Adhesive T2', 'Cemtite Tile Adhesive T3', 'Cemtite Plaster', 'Cemtite Block Adhesive'
];

const BulkOrderPage = () => {
  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '', product: '', grade: '', quantity_bags: '', delivery_location: '', delivery_date: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { trackPageView('/bulk-order', 'Bulk Order Inquiry'); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/bulk-order`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, quantity_bags: parseInt(form.quantity_bags) || 100 }),
      });
      if (res.ok) {
        setSubmitted(true);
        trackEvent('bulk_order_submit', 'form', { product: form.product, qty: form.quantity_bags });
      }
    } catch {} finally { setLoading(false); }
  };

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  if (submitted) return (
    <div className="min-h-screen bg-[#0a0a0b] pt-28 pb-20 px-4 flex items-center justify-center" data-testid="bulk-order-page">
      <SEOHead
        title="Bulk Order"
        description="Place bulk cement orders directly with Avi Cement. Competitive pricing for contractors and builders on Lion, Hathi, Siddhee cement and more."
        path="/bulk-order"
      />
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center max-w-md">
        <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-emerald-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">Inquiry Received!</h2>
        <p className="text-gray-400">Humari sales team 24 ghante mein aapse contact karegi pricing aur delivery details ke liye.</p>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0b] pt-28 pb-20 px-4" data-testid="bulk-order-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto text-center mb-10">
        <span className="text-[#E85A1C] text-xs font-semibold tracking-[0.2em] uppercase">Bulk Orders</span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 mb-4">
          100+ Bags? <span className="text-[#F5A623]">Best Price</span> Milega
        </h1>
        <p className="text-gray-400 text-sm max-w-md mx-auto">Bulk orders ke liye special pricing, priority delivery, aur dedicated support.</p>
      </motion.div>

      <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        onSubmit={handleSubmit} className="max-w-2xl mx-auto bg-[#111113] border border-gray-800 rounded-2xl p-6 md:p-8" data-testid="bulk-order-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            { key: 'name', label: 'Your Name', icon: User, type: 'text', required: true },
            { key: 'company', label: 'Company Name', icon: Building2, type: 'text', required: true },
            { key: 'phone', label: 'Phone Number', icon: Phone, type: 'tel', required: true },
            { key: 'email', label: 'Email', icon: Mail, type: 'email', required: true },
          ].map(f => (
            <div key={f.key}>
              <label className="text-gray-400 text-xs mb-1.5 block">{f.label} *</label>
              <div className="relative">
                <f.icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                <input type={f.type} required={f.required} value={form[f.key]} onChange={e => update(f.key, e.target.value)}
                  data-testid={`bulk-order-${f.key}`}
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-[#0a0a0b] border border-gray-800 text-white text-sm focus:outline-none focus:border-[#E85A1C] transition-colors" />
              </div>
            </div>
          ))}
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">Product *</label>
            <div className="relative">
              <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
              <select required value={form.product} onChange={e => update('product', e.target.value)} data-testid="bulk-order-product"
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-[#0a0a0b] border border-gray-800 text-white text-sm focus:outline-none focus:border-[#E85A1C] appearance-none">
                <option value="">Select product</option>
                {products.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">Quantity (bags) *</label>
            <input type="number" required min="100" value={form.quantity_bags} onChange={e => update('quantity_bags', e.target.value)}
              placeholder="Min 100 bags" data-testid="bulk-order-quantity"
              className="w-full px-4 py-3 rounded-lg bg-[#0a0a0b] border border-gray-800 text-white text-sm focus:outline-none focus:border-[#E85A1C]" />
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">Delivery Location *</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
              <input type="text" required value={form.delivery_location} onChange={e => update('delivery_location', e.target.value)}
                data-testid="bulk-order-location"
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-[#0a0a0b] border border-gray-800 text-white text-sm focus:outline-none focus:border-[#E85A1C]" />
            </div>
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">Preferred Delivery Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
              <input type="date" value={form.delivery_date} onChange={e => update('delivery_date', e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-[#0a0a0b] border border-gray-800 text-white text-sm focus:outline-none focus:border-[#E85A1C]" />
            </div>
          </div>
        </div>
        <div className="mt-5">
          <label className="text-gray-400 text-xs mb-1.5 block">Additional Message</label>
          <textarea rows={3} value={form.message} onChange={e => update('message', e.target.value)}
            className="w-full px-4 py-3 rounded-lg bg-[#0a0a0b] border border-gray-800 text-white text-sm focus:outline-none focus:border-[#E85A1C] resize-none" />
        </div>
        <button type="submit" disabled={loading} data-testid="bulk-order-submit"
          className="mt-6 w-full flex items-center justify-center gap-2 bg-[#E85A1C] hover:bg-[#d14e15] text-white font-medium py-4 rounded-xl transition-all disabled:opacity-50">
          {loading ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Send className="w-4 h-4" /> Submit Bulk Order Inquiry</>}
        </button>
      </motion.form>
    </div>
  );
};

export default BulkOrderPage;
